import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import {
  Video,
  Upload,
  Play,
  Trash2,
  Copy,
  RefreshCw,
  Clock,
  Sparkles,
  Code,
  Terminal,
  X,
  ChevronDown,
  ChevronUp,
  Settings,
  Info,
} from 'lucide-react';

import {
  useInitiateMultipartMutation,
  useGetPresignedUrlMutation,
  useCompleteMultipartMutation,
  useAbortMultipartMutation,
  useSaveVideoMutation,
  useDeleteVideoMutation,
  useLazyGetVideoQuery,
  VideoData,
} from '@/store/api/videoGenerationsApi';

const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

interface LogEntry {
  timestamp: string;
  type: 'info' | 'success' | 'error' | 'request' | 'response';
  message: string;
  data?: any;
}

interface ChunkStatus {
  partNumber: number;
  status: 'pending' | 'uploading' | 'success' | 'failed';
  progress: number;
  size: number;
}

function ConfirmDeleteVideoDialog({
  videoTitle,
  onConfirm,
  onCancel,
  isLoading,
}: {
  videoTitle: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200 border border-gray-105">
        <div className="bg-[linear-gradient(to_right,#082261,#34065c)] p-5 text-white flex items-center gap-3">
          <div className="bg-white/20 rounded-full p-2">
            <Trash2 className="h-5 w-5" />
          </div>
          <div>
            <p className="font-semibold font-spectral">Confirm Delete</p>
            <p className="text-white/70 text-xs mt-0.5">This action cannot be undone</p>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600">
            Are you sure you want to permanently delete the video <strong>"{videoTitle}"</strong>?
          </p>
          <div className="flex gap-3 justify-end">
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="px-4 py-2 text-sm rounded-lg bg-red-650 text-white border-gray-300 font-semibold bg-red-700 hover:bg-red-500 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  Delete
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Custom Fallback Force Delete Dialog
function ConfirmForceDeleteDialog({
  errorMsg,
  onConfirm,
  onCancel,
}: {
  errorMsg: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200 border border-gray-105">
        <div className="bg-amber-600 p-5 text-white flex items-center gap-3">
          <div className="bg-white/20 rounded-full p-2">
            <Info className="h-5 w-5" />
          </div>
          <div>
            <p className="font-semibold font-spectral">Server Deletion Error</p>
            <p className="text-white/70 text-xs mt-0.5">Database sync failed</p>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600">
            The server returned an error:{' '}
            <code className="bg-red-50 text-red-650 px-1.5 py-0.5 rounded text-xs break-all block mt-1 font-mono">
              {errorMsg}
            </code>
            <span className="block mt-2">
              Would you like to force remove this video record from your local dashboard view
              anyway?
            </span>
          </p>
          <div className="flex gap-3 justify-end">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 text-sm rounded-lg bg-amber-600 text-white font-semibold hover:bg-amber-700 transition-colors"
            >
              Force Remove
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function VideoGenerationsPage() {
  // Form State
  const [videoTitle, setVideoTitle] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const folder = import.meta.env.VITE_VIDEO_FOLDER_PATH || 'videos';
  const [chunkSizeMb, setChunkSizeMb] = useState(5); // Default 5MB
  const isDemoMode = false;

  // Upload Tracking State
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSpeed, setUploadSpeed] = useState('');
  const [eta, setEta] = useState('');
  const [activeUploadId, setActiveUploadId] = useState<string | null>(null);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [chunks, setChunks] = useState<ChunkStatus[]>([]);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [showLogs, setShowLogs] = useState(true);

  // Gallery & Dialogs State
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedVideo, setSelectedVideo] = useState<VideoData | null>(null);
  const [localVideos, setLocalVideos] = useState<VideoData[]>([]);
  const [videoToDelete, setVideoToDelete] = useState<VideoData | null>(null);
  const [forceDeleteConfig, setForceDeleteConfig] = useState<{ id: string; error: string } | null>(
    null
  );

  // Refs for tracking speed and cancellation
  const uploadStartTime = useRef<number>(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const logsContainerRef = useRef<HTMLDivElement | null>(null);

  // RTK Query Mutations & Queries
  const [initiateMultipart, { isLoading: isInitiating }] = useInitiateMultipartMutation();
  const [getPresignedUrl] = useGetPresignedUrlMutation();
  const [completeMultipart, { isLoading: isCompleting }] = useCompleteMultipartMutation();
  const [abortMultipart] = useAbortMultipartMutation();
  const [saveVideo] = useSaveVideoMutation();
  const [deleteVideo, { isLoading: isDeletingVideo }] = useDeleteVideoMutation();

  // Lazy query for individual video status refresh
  const [getVideoTrigger] = useLazyGetVideoQuery();
  const [isRefreshingVideo, setIsRefreshingVideo] = useState(false);

  // Automatically scroll logs container to bottom (without scrolling the main page)
  useEffect(() => {
    if (logsContainerRef.current) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
    }
  }, [logs]);

  // Ensure window starts from the top on page load
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // Load local videos from local storage on mount
  useEffect(() => {
    const stored = localStorage.getItem('astro_video_generations');
    if (stored) {
      try {
        setLocalVideos(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse local storage videos', e);
      }
    }
  }, []);

  // Sync local videos to local storage when state changes
  const saveLocalVideos = (videos: VideoData[]) => {
    setLocalVideos(videos);
    localStorage.setItem('astro_video_generations', JSON.stringify(videos));
  };

  // Sync individual video status from server
  const handleRefreshVideoStatus = async (id: string) => {
    if (id.startsWith('sim-db-')) {
      toast('Simulated video status is always mock Pending.');
      return;
    }

    setIsRefreshingVideo(true);
    try {
      addLog('request', `Syncing latest status for video ID: ${id} from server...`);
      const response = await getVideoTrigger(id).unwrap();

      if (response.success && response.data) {
        const updatedVideo = response.data;
        addLog('response', `Status synced successfully: ${updatedVideo.videoStatus}`, updatedVideo);

        // Update local list with new status
        const updatedList = localVideos.map((v) =>
          v.id === id
            ? { ...v, videoStatus: updatedVideo.videoStatus, videoUrl: updatedVideo.videoUrl }
            : v
        );
        saveLocalVideos(updatedList);
        toast.success(`Synced status: ${updatedVideo.videoStatus}`);
      } else {
        addLog('response', `Server response did not contain valid data`, response);
      }
    } catch (err: any) {
      console.error('Failed to sync video status:', err);
      let errMsg = 'Failed to sync status';
      if (err?.data?.message) errMsg = err.data.message;
      addLog('error', `Status sync failed: ${errMsg}`, err);
      toast.error(errMsg);
    } finally {
      setIsRefreshingVideo(false);
    }
  };

  // Helper to add a developer log entry
  const addLog = useCallback((type: LogEntry['type'], message: string, data?: any) => {
    const time = new Date().toLocaleTimeString();
    setLogs((prev) => [...prev, { timestamp: time, type, message, data }]);
  }, []);

  // Dropzone setup
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        if (file) {
          setSelectedFile(file);
          if (!videoTitle) {
            // Default title to file name without extension
            setVideoTitle(file.name.replace(/\.[^/.]+$/, ''));
          }
          toast.success(`Selected file: ${file.name}`);
        }
      }
    },
    [videoTitle]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'video/*': ['.mp4', '.mov', '.avi', '.mkv', '.webm'],
    },
    multiple: false,
    disabled: isUploading,
  });

  // Cancel/Abort upload
  const handleAbort = async () => {
    if (!isUploading) return;

    addLog('info', 'Aborting upload process...');

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (activeUploadId && activeKey) {
      try {
        addLog('request', 'Aborting multipart upload on server...', {
          key: activeKey,
          uploadId: activeUploadId,
        });
        if (isDemoMode) {
          addLog('response', 'Aborted successfully (Simulated)');
        } else {
          await abortMultipart({ key: activeKey, uploadId: activeUploadId }).unwrap();
          addLog('response', 'Aborted successfully (Server)');
        }
      } catch (err: any) {
        addLog('error', 'Abort API call failed', err);
      }
    }

    setIsUploading(false);
    setUploadProgress(0);
    setActiveUploadId(null);
    setActiveKey(null);
    setChunks([]);
    toast.error('Upload cancelled');
  };

  // Perform multipart upload
  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error('Please select a video file first');
      return;
    }
    if (!videoTitle.trim()) {
      toast.error('Please enter a video title');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setLogs([]);
    uploadStartTime.current = Date.now();
    abortControllerRef.current = new AbortController();

    const file = selectedFile;
    const size = file.size;
    const chunkSize = chunkSizeMb * 1024 * 1024;
    const totalParts = Math.ceil(size / chunkSize);

    addLog('info', `Starting upload for "${videoTitle}" (${formatBytes(size)})`);
    addLog('info', `Configured part size: ${chunkSizeMb}MB. Total parts to upload: ${totalParts}`);

    // Set initial chunk grid status
    const initialChunks: ChunkStatus[] = [];
    for (let i = 1; i <= totalParts; i++) {
      const start = (i - 1) * chunkSize;
      const end = Math.min(start + chunkSize, size);
      initialChunks.push({
        partNumber: i,
        status: 'pending',
        progress: 0,
        size: end - start,
      });
    }
    setChunks(initialChunks);

    let uploadId = '';
    let s3Key = '';

    try {
      // Step 1: Initiate Multipart Upload
      addLog('request', 'Initiating multipart upload...', {
        fileName: file.name,
        fileType: file.type || 'video/mp4',
        folder,
      });

      if (isDemoMode) {
        // Simulate initiate delay
        await new Promise((resolve) => setTimeout(resolve, 800));
        uploadId = 'simulated-upload-id-' + Math.random().toString(36).substring(2, 15);
        s3Key = `${folder}/${Date.now()}-${file.name.replace(/\s+/g, '_')}`;
        addLog('response', 'Multipart upload initiated (Simulated)', { key: s3Key, uploadId });
      } else {
        const initRes = await initiateMultipart({
          fileName: file.name,
          fileType: file.type || 'video/mp4',
          folder,
        }).unwrap();
        const rawInit = initRes as any;
        uploadId = rawInit?.uploadId || rawInit?.data?.uploadId || '';
        s3Key = rawInit?.key || rawInit?.data?.key || '';
        if (!uploadId || !s3Key) {
          throw new Error(
            `Failed to initialize upload. Invalid uploadId (${uploadId}) or key (${s3Key}) received from server.`
          );
        }
        addLog('response', 'Multipart upload initiated successfully', initRes);
      }

      setActiveUploadId(uploadId);
      setActiveKey(s3Key);

      // Step 2: Upload Chunks one by one
      const uploadedParts: Array<{ ETag: string; PartNumber: number }> = [];

      for (let i = 0; i < totalParts; i++) {
        // Check if aborted
        if (abortControllerRef.current?.signal.aborted) {
          throw new Error('Upload aborted by user');
        }

        const partNum = i + 1;
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, size);
        const chunkBlob = file.slice(start, end);

        // Update status to uploading
        setChunks((prev) =>
          prev.map((c) => (c.partNumber === partNum ? { ...c, status: 'uploading' } : c))
        );

        addLog('request', `Generating presigned URL for Part ${partNum}/${totalParts}...`);

        let presignedUrlStr = '';
        if (isDemoMode) {
          await new Promise((resolve) => setTimeout(resolve, 400));
          presignedUrlStr = `https://simulated-s3-upload.amazonaws.com/${s3Key}?partNumber=${partNum}&uploadId=${uploadId}`;
          addLog('response', `Presigned URL generated (Simulated)`, { url: presignedUrlStr });
        } else {
          const presignedRes = await getPresignedUrl({
            key: s3Key,
            uploadId,
            partNumber: partNum,
          }).unwrap();

          if (typeof presignedRes === 'string') {
            presignedUrlStr = presignedRes;
          } else {
            const rawPresigned = presignedRes as any;
            presignedUrlStr =
              rawPresigned?.signedUrl ||
              rawPresigned?.url ||
              rawPresigned?.data?.signedUrl ||
              rawPresigned?.data?.url ||
              (typeof rawPresigned?.data === 'string' ? rawPresigned.data : '');
          }

          if (!presignedUrlStr) {
            throw new Error(
              `Failed to generate presigned S3 URL for Part ${partNum}. Received: ${JSON.stringify(presignedRes)}`
            );
          }

          addLog('response', `Presigned URL generated for Part ${partNum}`, presignedRes);
        }

        // Upload part
        addLog(
          'request',
          `Uploading Part ${partNum} raw binary (${formatBytes(chunkBlob.size)})...`
        );

        let eTag = '';
        if (isDemoMode) {
          // Simulate uploading chunks with ticks
          const ticks = 5;
          for (let tick = 1; tick <= ticks; tick++) {
            if (abortControllerRef.current?.signal.aborted) {
              throw new Error('Upload aborted by user');
            }
            await new Promise((resolve) => setTimeout(resolve, 200));
            const partProgress = (tick / ticks) * 100;

            // Update individual chunk progress
            setChunks((prev) =>
              prev.map((c) => (c.partNumber === partNum ? { ...c, progress: partProgress } : c))
            );

            // Update overall progress
            const bytesCompleted = start + chunkBlob.size * (tick / ticks);
            const overallPct = Math.round((bytesCompleted / size) * 100);
            setUploadProgress(overallPct);

            // Calculate Speed & ETA
            const elapsed = (Date.now() - uploadStartTime.current) / 1000; // seconds
            const speedBytesSec = bytesCompleted / elapsed;
            setUploadSpeed(`${formatBytes(speedBytesSec)}/s`);
            const remainingBytes = size - bytesCompleted;
            const remainingSeconds =
              speedBytesSec > 0 ? Math.round(remainingBytes / speedBytesSec) : 0;
            setEta(remainingSeconds > 0 ? `${remainingSeconds}s` : 'calculating...');
          }

          eTag = `"${Math.random().toString(16).substring(2, 10)}${Math.random().toString(16).substring(2, 10)}"`;
          addLog('response', `Part ${partNum} uploaded (Simulated ETag: ${eTag})`);
        } else {
          // Real S3 PUT Upload
          const xhr = new XMLHttpRequest();
          const uploadPromise = new Promise<string>((resolve, reject) => {
            xhr.open('PUT', presignedUrlStr, true);
            xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');

            xhr.upload.onprogress = (event) => {
              if (event.lengthComputable) {
                const partProgress = (event.loaded / event.total) * 100;
                setChunks((prev) =>
                  prev.map((c) => (c.partNumber === partNum ? { ...c, progress: partProgress } : c))
                );

                const bytesCompleted = start + event.loaded;
                const overallPct = Math.round((bytesCompleted / size) * 100);
                setUploadProgress(overallPct);

                // Calculate Speed & ETA
                const elapsed = (Date.now() - uploadStartTime.current) / 1000;
                const speedBytesSec = bytesCompleted / elapsed;
                setUploadSpeed(`${formatBytes(speedBytesSec)}/s`);
                const remainingBytes = size - bytesCompleted;
                const remainingSeconds =
                  speedBytesSec > 0 ? Math.round(remainingBytes / speedBytesSec) : 0;
                setEta(remainingSeconds > 0 ? `${remainingSeconds}s` : 'calculating...');
              }
            };

            xhr.onload = () => {
              if (xhr.status === 200) {
                const etagHeader =
                  xhr.getResponseHeader('ETag') ||
                  `"${Math.random().toString(16).substring(2, 10)}"`;
                resolve(etagHeader);
              } else {
                reject(new Error(`HTTP status ${xhr.status}`));
              }
            };

            xhr.onerror = () => reject(new Error('Network error during S3 upload'));
            xhr.onabort = () => reject(new Error('Upload aborted'));

            // Link abort controller
            abortControllerRef.current?.signal.addEventListener('abort', () => {
              xhr.abort();
            });

            xhr.send(chunkBlob);
          });

          eTag = await uploadPromise;
          addLog('response', `Part ${partNum} uploaded successfully. ETag: ${eTag}`);
        }

        // Set chunk status to success
        setChunks((prev) =>
          prev.map((c) => (c.partNumber === partNum ? { ...c, status: 'success' } : c))
        );

        uploadedParts.push({ ETag: eTag, PartNumber: partNum });
      }

      // Step 3: Complete Multipart Upload
      addLog('request', 'Completing multipart upload...', {
        key: s3Key,
        uploadId,
        parts: uploadedParts,
      });

      let finalVideoUrl = `https://s3.amazonaws.com/meraastro-video-bucket/${s3Key}`;

      if (isDemoMode) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        addLog('response', 'Multipart upload completed (Simulated)', {
          success: true,
          videoUrl: finalVideoUrl,
        });
      } else {
        const completeRes = await completeMultipart({
          key: s3Key,
          uploadId,
          parts: uploadedParts,
        }).unwrap();

        const rawComplete = completeRes as any;
        const resVideoUrl =
          rawComplete?.fileUrl ||
          rawComplete?.videoUrl ||
          rawComplete?.data?.fileUrl ||
          rawComplete?.data?.videoUrl;
        if (resVideoUrl) {
          finalVideoUrl = resVideoUrl;
        }
        addLog('response', 'Multipart upload completed successfully', completeRes);
      }

      // Step 4: Save Video to Database
      addLog('request', 'Saving video to database record...', {
        title: videoTitle,
        videoUrl: finalVideoUrl,
        s3Key,
        fileSize: size,
        status: 'Pending',
      });

      let dbRecord: VideoData;

      if (isDemoMode) {
        await new Promise((resolve) => setTimeout(resolve, 800));
        dbRecord = {
          id: 'sim-db-' + Math.random().toString(36).substring(2, 12),
          title: videoTitle,
          videoUrl: finalVideoUrl,
          localUrl: URL.createObjectURL(file), // Keep a reference to the local file for demo playback
          s3Key,
          fileSize: size,
          videoStatus: 'Pending',
          createdAt: new Date().toISOString().replace('T', ' ').substring(0, 19),
          updatedAt: new Date().toISOString().replace('T', ' ').substring(0, 19),
        };
        addLog('response', 'Video record saved successfully (Simulated)', dbRecord);
      } else {
        const saveRes = await saveVideo({
          title: videoTitle,
          videoUrl: finalVideoUrl,
          s3Key,
          fileSize: size,
          status: 'Pending',
        }).unwrap();
        const rawSave = saveRes as any;
        dbRecord = rawSave?.data || rawSave;
        addLog('response', 'Video record saved successfully', saveRes);
      }

      // Add to local storage tracking list
      const updatedList = [dbRecord, ...localVideos];
      saveLocalVideos(updatedList);

      toast.success('Video uploaded and saved successfully!');

      // Reset upload state
      setSelectedFile(null);
      setVideoTitle('');
      setIsUploading(false);
      setUploadProgress(100);
      setActiveUploadId(null);
      setActiveKey(null);
    } catch (err: any) {
      console.error('Upload Process Failed:', err);

      // Update uploading chunks to failed
      setChunks((prev) =>
        prev.map((c) =>
          c.status === 'uploading' || c.status === 'pending' ? { ...c, status: 'failed' } : c
        )
      );

      let errMsg = 'Upload failed';
      if (err?.message) {
        errMsg = err.message;
      } else if (err?.data?.message) {
        errMsg =
          typeof err.data.message === 'string'
            ? err.data.message
            : JSON.stringify(err.data.message);
      }

      addLog('error', `Process failed: ${errMsg}`, err);
      toast.error(errMsg);
      setIsUploading(false);

      // Try aborting in background if we initiated
      if (uploadId && s3Key && !abortControllerRef.current?.signal.aborted) {
        try {
          if (isDemoMode) {
            addLog('info', 'Cleaned up multipart session (Simulated)');
          } else {
            await abortMultipart({ key: s3Key, uploadId }).unwrap();
            addLog('info', 'Successfully aborted S3 multipart upload to clean up storage.');
          }
        } catch (abortErr) {
          console.warn('Cleanup abort failed', abortErr);
        }
      }
    }
  };

  // Copy to clipboard
  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success('S3 Video URL copied to clipboard');
  };

  // Trigger custom confirmation modal
  const handleDeleteVideoClick = (video: VideoData) => {
    setVideoToDelete(video);
  };

  // Confirm delete from modal
  const handleDeleteVideoConfirm = async () => {
    if (!videoToDelete) return;
    const { id } = videoToDelete;

    try {
      addLog('info', `Requesting delete for video ID: ${id}`);
      if (isDemoMode || id.startsWith('sim-db-')) {
        // Simulated delete
        await new Promise((resolve) => setTimeout(resolve, 500));
        addLog('success', `Video deleted successfully (Simulated)`);
      } else {
        await deleteVideo(id).unwrap();
        addLog('success', `Video deleted successfully from database`);
      }

      // Remove from local list
      const updatedList = localVideos.filter((v) => v.id !== id);
      saveLocalVideos(updatedList);

      toast.success('Video deleted successfully');
      setVideoToDelete(null);
    } catch (err: any) {
      console.error('Delete failed:', err);
      let errMsg = 'Failed to delete video';
      if (err?.data?.message) errMsg = err.data.message;
      else if (err?.message) errMsg = err.message;

      addLog('error', `Delete failed: ${errMsg}`, err);

      // Open the custom force delete fallback dialog
      setForceDeleteConfig({ id, error: errMsg });
      setVideoToDelete(null);
    }
  };

  // Perform force delete removal from local storage
  const handleForceDeleteConfirm = () => {
    if (!forceDeleteConfig) return;
    const { id } = forceDeleteConfig;
    const updatedList = localVideos.filter((v) => v.id !== id);
    saveLocalVideos(updatedList);
    toast.success('Removed from local dashboard list');
    setForceDeleteConfig(null);
  };

  // Get combined videos
  const getMergedVideos = (): VideoData[] => {
    let merged = [...localVideos];

    // Apply Filter status
    if (statusFilter !== 'all') {
      merged = merged.filter((v) => v.videoStatus.toLowerCase() === statusFilter.toLowerCase());
    }

    // Apply Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      merged = merged.filter(
        (v) =>
          v.title.toLowerCase().includes(q) ||
          v.s3Key.toLowerCase().includes(q) ||
          v.id.toLowerCase().includes(q)
      );
    }

    // Sort by createdAt descending
    return merged.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  };

  const filteredVideos = getMergedVideos();

  return (
    <div className="space-y-6 w-full mx-auto pb-12">
      {/* Title Header Card */}
      <div className="bg-[linear-gradient(to_right,#082261,#34065c)] rounded-2xl shadow-xl p-6 md:p-8 text-white relative overflow-hidden border border-white/10">
        <div className="absolute top-0 right-0 p-4 opacity-10">
          <Video className="w-40 h-40" />
        </div>

        <div className="relative z-10">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="bg-white/10 backdrop-blur-md text-xs font-semibold px-2.5 py-1 rounded-full text-indigo-200 border border-white/15 flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5 text-yellow-300 animate-pulse" />
                S3 Storage Enabled
              </span>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full border bg-green-500/20 text-green-300 border-green-500/30">
                Real Server Mode
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold font-spectral tracking-tight bg-clip-text bg-gradient-to-r from-white to-indigo-100">
              S3 Video Upload & Cloud Storage
            </h1>
            <p className="text-indigo-100/70 text-sm md:text-base max-w-xl">
              Initiate multipart uploads, split files into chunks, upload directly to S3 via
              presigned URLs, and register video records into the database.
            </p>
          </div>
        </div>
      </div>

      {/* Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Side: Upload Panel */}
        <div className="lg:col-span-7 space-y-6">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-md p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#082261]" />
              New Video Multipart Upload
            </h2>

            <form onSubmit={handleUpload} className="space-y-4">
              {/* Title input */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider block">
                  Video Title *
                </label>
                <input
                  type="text"
                  placeholder="Enter a descriptive title for this video"
                  value={videoTitle}
                  onChange={(e) => setVideoTitle(e.target.value)}
                  disabled={isUploading}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#082261]/20 focus:border-[#082261] transition-all text-sm"
                  required
                />
              </div>

              {/* Advanced config grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider block">
                    S3 Folder Path
                  </label>
                  <div className="w-full px-4 py-2.5 rounded-xl border border-gray-150 bg-gray-50/70 font-mono text-xs text-gray-500 flex items-center justify-between">
                    <span>{folder}</span>
                    <span className="text-[9px] bg-gray-200/80 text-gray-600 px-1.5 py-0.5 rounded font-sans uppercase font-bold tracking-wider">
                      Env Config
                    </span>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider flex items-center justify-between">
                    <span>Part Chunk Size</span>
                    <span className="text-[#082261] lowercase font-normal">Min 5MB for S3</span>
                  </label>
                  <select
                    value={chunkSizeMb}
                    onChange={(e) => setChunkSizeMb(Number(e.target.value))}
                    disabled={isUploading}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#082261]/20 focus:border-[#082261] text-sm bg-white transition-all"
                  >
                    <option value={5}>5 MB (Recommended)</option>
                    <option value={10}>10 MB</option>
                    <option value={20}>20 MB</option>
                    <option value={50}>50 MB</option>
                  </select>
                </div>
              </div>

              {/* Drag-and-drop Zone */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider block">
                  Select Video File *
                </label>

                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all duration-300 ${
                    isDragActive
                      ? 'border-[#082261] bg-[#082261]/5 shadow-inner scale-[0.99]'
                      : 'border-gray-200 hover:border-gray-300 bg-gray-50/50 hover:bg-gray-50'
                  } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <input {...getInputProps()} />
                  <div className="flex flex-col items-center justify-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center text-[#082261]">
                      {selectedFile ? (
                        <Video className="w-6 h-6 animate-pulse" />
                      ) : (
                        <Upload className="w-6 h-6" />
                      )}
                    </div>
                    {selectedFile ? (
                      <div className="space-y-1">
                        <p className="text-sm font-semibold text-gray-800 break-all">
                          {selectedFile.name}
                        </p>
                        <p className="text-xs text-gray-500 font-mono">
                          {formatBytes(selectedFile.size)} | {selectedFile.type || 'video/mp4'}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-gray-700">
                          Drag and drop your video file here, or{' '}
                          <span className="text-[#082261] font-semibold underline">browse</span>
                        </p>
                        <p className="text-xs text-gray-400">
                          Supports MP4, MOV, MKV, AVI, WEBM up to 1GB
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Info alert about minimum S3 multipart parts */}
              {selectedFile && selectedFile.size < 5 * 1024 * 1024 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2.5 items-start">
                  <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700 leading-normal">
                    This file is smaller than 5MB. In a real environment, S3 multipart uploads are
                    only strictly needed for files &gt; 5MB. Since it is small, it will proceed as a
                    single part.
                  </p>
                </div>
              )}

              {/* Submit & Cancel Actions */}
              {!isUploading ? (
                <button
                  type="submit"
                  disabled={!selectedFile || !videoTitle.trim()}
                  className="w-full py-3 bg-[linear-gradient(to_right,#082261,#34065c)] hover:opacity-95 text-white font-semibold font-spectral rounded-xl text-sm uppercase transition-all flex items-center justify-center gap-2 shadow-lg disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.99]"
                >
                  <Upload className="w-4 h-4" />
                  Start Multipart Upload
                </button>
              ) : (
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleAbort}
                    className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold font-spectral rounded-xl text-sm uppercase transition-all flex items-center justify-center gap-2 shadow-md active:scale-[0.99]"
                  >
                    <X className="w-4 h-4" />
                    Cancel & Abort Session
                  </button>
                </div>
              )}
            </form>
          </div>

          {/* Active Upload Live Tracker Card */}
          {isUploading && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-md p-6 space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-300">
              <div className="flex items-center justify-between border-b pb-3">
                <div>
                  <h3 className="font-bold text-gray-800 text-sm">Upload Status Monitor</h3>
                  <p className="text-xs text-gray-400 font-mono mt-0.5 truncate max-w-xs md:max-w-md">
                    ID: {activeUploadId || 'generating...'}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 py-1 px-2.5 rounded-full border">
                  <Clock className="w-3.5 h-3.5 text-indigo-500 animate-spin" />
                  <span>Processing Chunks</span>
                </div>
              </div>

              {/* Visual Stats Row */}
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-gray-50 p-2.5 rounded-xl border">
                  <p className="text-[10px] uppercase font-bold text-gray-400">Total Progress</p>
                  <p className="text-lg font-bold text-gray-800">{uploadProgress}%</p>
                </div>
                <div className="bg-gray-50 p-2.5 rounded-xl border">
                  <p className="text-[10px] uppercase font-bold text-gray-400">Upload Speed</p>
                  <p className="text-lg font-bold text-indigo-600">{uploadSpeed || '—'}</p>
                </div>
                <div className="bg-gray-50 p-2.5 rounded-xl border">
                  <p className="text-[10px] uppercase font-bold text-gray-400">Time Remaining</p>
                  <p className="text-lg font-bold text-gray-800">{eta || '—'}</p>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs font-semibold text-gray-600">
                  <span>Byte stream upload</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden relative">
                  <div
                    className="h-full bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                  {/* Glowing micro-animation */}
                  <div
                    className="absolute top-0 bottom-0 bg-white/30 w-8 blur-sm animate-[pulse_1s_infinite]"
                    style={{ left: `calc(${uploadProgress}% - 16px)` }}
                  />
                </div>
              </div>

              {/* Part Chunk Grid Representation */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                    S3 Part Buffer Grid ({chunks.length} parts)
                  </span>
                  <div className="flex gap-3 text-[10px] font-semibold text-gray-500">
                    <span className="flex items-center gap-1">
                      <span className="w-2.5 h-2.5 bg-gray-100 border rounded-sm" /> Pending
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2.5 h-2.5 bg-blue-500 rounded-sm animate-pulse" />{' '}
                      Uploading
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2.5 h-2.5 bg-green-500 rounded-sm" /> Success
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2.5 h-2.5 bg-red-500 rounded-sm" /> Failed
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-2 p-3 bg-gray-50 rounded-xl border border-dashed">
                  {chunks.map((chunk) => (
                    <div
                      key={chunk.partNumber}
                      title={`Part ${chunk.partNumber}: ${formatBytes(chunk.size)} (${Math.round(chunk.progress)}%)`}
                      className={`h-10 rounded-lg flex flex-col items-center justify-center border text-xs font-semibold transition-all relative ${
                        chunk.status === 'success'
                          ? 'bg-green-50 text-green-700 border-green-200'
                          : chunk.status === 'uploading'
                            ? 'bg-blue-50 text-blue-700 border-blue-300 shadow-md animate-pulse scale-[1.03] z-10'
                            : chunk.status === 'failed'
                              ? 'bg-red-50 text-red-700 border-red-200'
                              : 'bg-white text-gray-400 border-gray-100'
                      }`}
                    >
                      <span>P{chunk.partNumber}</span>
                      {chunk.status === 'uploading' && (
                        <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600 overflow-hidden rounded-b-lg">
                          <div
                            className="h-full bg-blue-400"
                            style={{ width: `${chunk.progress}%` }}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Side: Saved Videos & Logs */}
        <div className="lg:col-span-5 space-y-6">
          {/* Saved Videos Gallery Panel */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-md p-6 space-y-4">
            <div className="flex items-center justify-between border-b pb-3">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Video className="w-5 h-5 text-[#082261]" />
                S3 Upload Library
              </h2>
              <button
                onClick={async () => {
                  const realVideos = localVideos.filter((v) => !v.id.startsWith('sim-db-'));
                  if (realVideos.length === 0) {
                    toast('No database video records to sync.');
                    return;
                  }
                  toast.success('Syncing all video statuses...');
                  for (const video of realVideos) {
                    await handleRefreshVideoStatus(video.id);
                  }
                }}
                disabled={isRefreshingVideo}
                className="p-1.5 text-gray-500 hover:text-indigo-600 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                title="Sync all video statuses"
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshingVideo ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {/* Filter and search bar */}
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                placeholder="Search by title or key..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 px-3 py-1.5 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-1 focus:ring-[#082261]"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-[#082261]"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            {/* Video List */}
            <div className="space-y-3 max-h-[350px] overflow-y-auto pr-1">
              {filteredVideos.length === 0 ? (
                <div className="text-center py-10 border border-dashed rounded-xl bg-gray-50/50">
                  <Video className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-xs text-gray-500 font-medium">
                    No videos found matching filters
                  </p>
                  <p className="text-[10px] text-gray-400 mt-1">
                    Upload a video to see it list here.
                  </p>
                </div>
              ) : (
                filteredVideos.map((video) => (
                  <div
                    key={video.id}
                    className="p-3 bg-gray-50 hover:bg-gray-100/70 border border-gray-150 rounded-xl transition-all flex items-center justify-between gap-3 group relative overflow-hidden"
                  >
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <span
                          className={`w-2 h-2 rounded-full ${
                            video.videoStatus === 'Success'
                              ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]'
                              : video.videoStatus === 'Failed'
                                ? 'bg-red-500'
                                : 'bg-amber-500 animate-pulse'
                          }`}
                        />
                        <h4
                          className="font-semibold text-gray-800 text-xs truncate max-w-[200px]"
                          title={video.title}
                        >
                          {video.title}
                        </h4>
                      </div>

                      <p
                        className="text-[10px] text-gray-500 truncate font-mono"
                        title={video.s3Key}
                      >
                        Key: {video.s3Key}
                      </p>

                      <div className="flex items-center gap-3 text-[10px] text-gray-400 font-medium">
                        <span>{formatBytes(video.fileSize)}</span>
                        <span>•</span>
                        <span>{video.createdAt.split(' ')[0]}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 shrink-0">
                      {/* Status sync for non-simulated items */}
                      {!video.id.startsWith('sim-db-') && (
                        <button
                          onClick={() => handleRefreshVideoStatus(video.id)}
                          disabled={isRefreshingVideo}
                          className="p-1.5 bg-amber-50 text-amber-700 hover:bg-amber-600 hover:text-white rounded-lg transition-colors border border-amber-100 disabled:opacity-50"
                          title="Sync Status from Server"
                        >
                          <RefreshCw
                            className={`w-3.5 h-3.5 ${isRefreshingVideo ? 'animate-spin' : ''}`}
                          />
                        </button>
                      )}
                      <button
                        onClick={() => setSelectedVideo(video)}
                        className="p-1.5 bg-indigo-50 text-indigo-700 hover:bg-[#082261] hover:text-white rounded-lg transition-colors border border-indigo-100"
                        title="Play Video"
                      >
                        <Play className="w-3.5 h-3.5 fill-current" />
                      </button>
                      <button
                        onClick={() => handleCopyUrl(video.videoUrl)}
                        className="p-1.5 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors border"
                        title="Copy S3 URL"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDeleteVideoClick(video)}
                        className="p-1.5 bg-red-50 text-red-600 hover:bg-red-600 hover:text-white rounded-lg transition-colors border border-red-100"
                        title="Delete Record"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Dev API Log Panel */}
          <div className="bg-gray-950 rounded-2xl shadow-2xl overflow-hidden border border-gray-800 text-gray-300 font-mono relative">
            {/* Terminal Window Title Bar (Mac-Style) */}
            <div
              className="bg-gray-900 px-4 py-3 border-b border-gray-800 flex items-center justify-between cursor-pointer hover:bg-gray-850/80 transition-colors select-none"
              onClick={() => setShowLogs(!showLogs)}
            >
              <div className="flex items-center gap-3">
                <div className="flex gap-1.5 shrink-0">
                  <span
                    className="w-3 h-3 rounded-full bg-rose-500/80 hover:bg-rose-500 transition-colors cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      setLogs([]);
                    }}
                    title="Clear console"
                  />
                  <span className="w-3 h-3 rounded-full bg-amber-500/80" />
                  <span className="w-3 h-3 rounded-full bg-emerald-500/80" />
                </div>
                <div className="h-4 w-[1px] bg-gray-800 mx-1" />
                <div className="flex items-center gap-2">
                  <Terminal className="w-3.5 h-3.5 text-indigo-400" />
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider font-sans">
                    Dev Transaction Console
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {logs.length > 0 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setLogs([]);
                    }}
                    className="text-[9px] text-gray-500 hover:text-gray-300 hover:bg-gray-800 px-2 py-0.5 rounded border border-gray-800 transition-colors font-sans"
                  >
                    Clear Logs
                  </button>
                )}
                {showLogs ? (
                  <ChevronUp className="w-3.5 h-3.5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
                )}
              </div>
            </div>

            {/* Log Output Body */}
            {showLogs && (
              <div
                ref={logsContainerRef}
                className="p-4 space-y-3 h-[250px] overflow-y-auto text-[11px] leading-relaxed select-text bg-gray-950 scrollbar-thin scrollbar-thumb-gray-800 scrollbar-track-transparent"
              >
                {logs.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center text-gray-600 gap-2 py-10 select-none">
                    <Code className="w-6 h-6 text-gray-700 animate-pulse" />
                    <span className="text-[10.5px] font-semibold text-gray-500">
                      Transaction console is active & listening...
                    </span>
                    <span className="text-[9.5px] text-gray-600 max-w-[200px] leading-normal font-sans">
                      Submit an upload or click sync to monitor HTTP requests.
                    </span>
                  </div>
                ) : (
                  logs.map((log, idx) => {
                    let badgeBg = 'bg-gray-850 text-gray-400 border-gray-850';
                    let textClass = 'text-gray-300';
                    let logPrefix = 'LOG';

                    if (log.type === 'request') {
                      badgeBg = 'bg-indigo-950/80 text-indigo-300 border-indigo-900/50';
                      textClass = 'text-indigo-200';
                      logPrefix = 'HTTP REQ';
                    } else if (log.type === 'response') {
                      badgeBg = 'bg-emerald-950/80 text-emerald-300 border-emerald-900/50';
                      textClass = 'text-emerald-200';
                      logPrefix = 'API RES';
                    } else if (log.type === 'error') {
                      badgeBg = 'bg-rose-950/80 text-rose-300 border-rose-900/50';
                      textClass = 'text-rose-200';
                      logPrefix = 'ERROR';
                    } else if (log.type === 'success') {
                      badgeBg = 'bg-green-950/80 text-green-300 border-green-900/50';
                      textClass = 'text-green-200';
                      logPrefix = 'SUCCESS';
                    }

                    return (
                      <div
                        key={idx}
                        className={`space-y-1.5 p-2.5 rounded-xl border bg-gray-900/40 border-gray-900 transition-all hover:bg-gray-900/60`}
                      >
                        <div className="flex items-center gap-2 text-[9px]">
                          <span
                            className={`px-2 py-0.5 rounded-md font-bold uppercase border tracking-wider shrink-0 ${badgeBg}`}
                          >
                            {logPrefix}
                          </span>
                          <span className="text-gray-600 font-medium shrink-0 font-sans">
                            {log.timestamp}
                          </span>
                          <div className="h-[1px] bg-gray-900 flex-1" />
                        </div>

                        <div className={`font-semibold font-mono break-words ${textClass}`}>
                          {log.message}
                        </div>

                        {log.data && (
                          <pre className="mt-1 p-2 bg-black/60 rounded-lg text-[9px] overflow-x-auto text-gray-400 border border-gray-900 max-h-[120px] select-text scrollbar-thin">
                            {JSON.stringify(log.data, null, 2)}
                          </pre>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Video Player Modal overlay */}
      {selectedVideo &&
        createPortal(
          <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden max-w-3xl w-full shadow-2xl relative">
              {/* Header info */}
              <div className="p-4 border-b border-gray-800 bg-gray-950 text-white flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-sm font-spectral">{selectedVideo.title}</h3>
                  <p className="text-[10px] text-gray-500 font-mono mt-0.5 truncate max-w-xs md:max-w-md">
                    S3 Link: {selectedVideo.videoUrl}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedVideo(null)}
                  className="p-1 text-gray-400 hover:text-white rounded-lg hover:bg-gray-850"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Video Box */}
              <div className="bg-black aspect-video flex items-center justify-center relative group">
                <video
                  src={selectedVideo.localUrl || selectedVideo.videoUrl}
                  controls
                  autoPlay
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    console.warn('Video load error', e);
                  }}
                />
              </div>

              {/* Meta Table Info */}
              <div className="p-4 bg-gray-950 border-t border-gray-800 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                <div>
                  <span className="text-gray-500 block">Database ID</span>
                  <span
                    className="font-mono text-gray-300 font-semibold truncate block"
                    title={selectedVideo.id}
                  >
                    {selectedVideo.id}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block">S3 Bucket Path</span>
                  <span
                    className="font-mono text-gray-300 font-semibold truncate block"
                    title={selectedVideo.s3Key}
                  >
                    {selectedVideo.s3Key}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block">File Size</span>
                  <span className="text-gray-300 font-semibold">
                    {formatBytes(selectedVideo.fileSize)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block">Date Added</span>
                  <span className="text-gray-300 font-semibold">{selectedVideo.createdAt}</span>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Custom Confirm Delete Video Dialog */}
      {videoToDelete &&
        createPortal(
          <ConfirmDeleteVideoDialog
            videoTitle={videoToDelete.title}
            onConfirm={handleDeleteVideoConfirm}
            onCancel={() => setVideoToDelete(null)}
            isLoading={isDeletingVideo}
          />,
          document.body
        )}

      {/* Custom Fallback Force Delete Dialog */}
      {forceDeleteConfig &&
        createPortal(
          <ConfirmForceDeleteDialog
            errorMsg={forceDeleteConfig.error}
            onConfirm={handleForceDeleteConfirm}
            onCancel={() => setForceDeleteConfig(null)}
          />,
          document.body
        )}
    </div>
  );
}
